import { Component, lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { firstBadEquation, latexCheckerReady, loadLatexChecker } from "../../lib/latexCheck";
import { ANSWER_MAX, equationsOf, fromAnswer } from "../../lib/theoryAnswer";
import { clearDraft, readDraft, saveDraft } from "../../lib/theoryDraft";

/**
 * The theory answer box: the rich editor when it's switched on and loads,
 * the plain textarea otherwise.
 *
 * Whatever happens to the rich editor, a student can always answer. Its code
 * is fetched only when needed; if that fetch fails (a flaky connection) or the
 * editor breaks while drawing, the plain box takes over with the same answer
 * in it, and says nothing alarming.
 *
 * The answer is kept as a draft on the device per question, so a refresh
 * doesn't lose it, and is cleared once the answer is marked.
 */

/** `VITE_RICH_THEORY=on` switches the rich editor on. Off by default while it's trialled. */
export const richTheoryEnabled = () => import.meta.env.VITE_RICH_THEORY === "on";

/** Loaded on first use. Exported so tests can make the load fail. */
export const loadRichEditor = () => import("./RichAnswerEditor");

const RichAnswerEditor = lazy(() => loadRichEditor());

export interface AnswerCheck {
  /** Why the answer can't be marked yet, or null. */
  problem: string | null;
}

/**
 * What stops an answer being marked: the length cap, or an equation that
 * won't draw. Equations are checked by KaTeX, which loads on first use; until
 * it has, an answer with equations waits a moment rather than being sent
 * unchecked. An answer with no equations never waits.
 */
export function checkAnswer(answer: string): AnswerCheck {
  if (answer.length > ANSWER_MAX) {
    return {
      problem: `Your answer is ${answer.length.toLocaleString("en-NG")} characters. The most that can be marked is ${ANSWER_MAX.toLocaleString("en-NG")}.`,
    };
  }
  const equations = equationsOf(fromAnswer(answer));
  if (equations.length && !latexCheckerReady()) {
    return { problem: "Checking your equations…" };
  }
  const bad = firstBadEquation(equations);
  return {
    problem: bad
      ? `Equation ${bad.index + 1} can't be shown, so it can't be marked: ${bad.reason}. Tap it to fix it.`
      : null,
  };
}

/**
 * The answer's check, kept current: loads the equation checker as soon as the
 * answer holds an equation, then re-checks. If it can't load (offline), the
 * equations are taken as written rather than blocking the student forever;
 * the marker still reads them.
 */
export function useAnswerCheck(answer: string): AnswerCheck {
  const [, setLoaded] = useState(latexCheckerReady());
  const [unavailable, setUnavailable] = useState(false);
  const needsChecker = /\\\(/.test(answer) && !latexCheckerReady();
  useEffect(() => {
    if (!needsChecker) return;
    let active = true;
    void loadLatexChecker().then((ok) => {
      if (!active) return;
      if (ok) setLoaded(true);
      else setUnavailable(true);
    });
    return () => {
      active = false;
    };
  }, [needsChecker]);
  const check = checkAnswer(answer);
  if (unavailable && check.problem === "Checking your equations…") return { problem: null };
  return check;
}

class Fallback extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: unknown) {
    console.warn("theory.rich_editor_failed", error);
  }
  override render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

interface Props {
  questionId: string;
  subject: string;
  value: string;
  onChange: (answer: string) => void;
  disabled?: boolean | undefined;
  /** Set once the answer is marked: the draft is no longer needed. */
  marked?: boolean | undefined;
}

export function TheoryAnswerInput({
  questionId,
  subject,
  value,
  onChange,
  disabled = false,
  marked = false,
}: Props) {
  const rich = richTheoryEnabled();

  // A draft from an earlier visit to this question comes back once, on arrival.
  const [restoredFor, setRestoredFor] = useState<string | null>(null);
  useEffect(() => {
    if (!rich || restoredFor === questionId) return;
    setRestoredFor(questionId);
    const draft = readDraft(questionId);
    if (draft && !value) onChange(draft);
  }, [rich, questionId, restoredFor, value, onChange]);

  useEffect(() => {
    if (!rich) return;
    if (marked) clearDraft(questionId);
    else if (restoredFor === questionId) saveDraft(questionId, value);
  }, [rich, questionId, value, marked, restoredFor]);

  const plain = (
    <textarea
      id="theory-answer"
      rows={8}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      placeholder="Write your answer as you would in the exam…"
      className="mt-2 w-full resize-y rounded-[var(--radius-control)] border border-border-strong bg-surface p-3.5 text-[14.5px] leading-[1.55] text-text outline-none placeholder:text-text-muted focus:border-accent disabled:opacity-70"
    />
  );

  if (!rich) return plain;

  /*
   * While the editor's code is fetched, a placeholder of the same size, not
   * the plain box. A working box here took a student's first click and words,
   * then vanished when the editor arrived, and focus went with it: typing
   * "first" left "f". The plain box is kept for when the editor can't load.
   */
  const loading = (
    <div
      role="status"
      className="mt-2 flex min-h-[12rem] w-full items-center justify-center rounded-[var(--radius-control)] border border-border-strong bg-surface text-[13px] text-text-muted"
    >
      Getting the answer box ready…
    </div>
  );

  const near = value.length > ANSWER_MAX * 0.9;
  return (
    <>
      <Fallback fallback={plain}>
        <Suspense fallback={loading}>
          <RichAnswerEditor
            id="theory-answer"
            value={value}
            onChange={onChange}
            subject={subject}
            disabled={disabled}
          />
        </Suspense>
      </Fallback>
      {near ? (
        <p
          className={`m-0 mt-1 text-right text-[11.5px] tabular ${value.length > ANSWER_MAX ? "text-negative" : "text-text-muted"}`}
        >
          {value.length.toLocaleString("en-NG")} / {ANSWER_MAX.toLocaleString("en-NG")}
        </p>
      ) : null}
    </>
  );
}
